import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getTalkingHeadVoiceConfig } from './voicePreferences';
import {
  AVATAR_CHANGED_EVENT,
  DEFAULT_ASSISTANT_NAME,
  getAssistantNameForId,
  readStoredAvatar,
} from './avatarCatalog';

// One avatar for the whole app (mounted once in AppShell). Pages talk to it
// through this context instead of holding their own TalkingHead instance, so
// the caregiver always sees the same assistant and it never reloads between
// pages.

const hasBrowserSpeechSynthesisSupport = () =>
  typeof window !== 'undefined' &&
  !!window.speechSynthesis &&
  typeof window.SpeechSynthesisUtterance === 'function';

const noop = () => {};

// What the assistant is doing right now, shown beside the avatar so the
// caregiver always knows whether to talk or wait.
export const AVATAR_STATUS = {
  idle: 'idle',
  listening: 'listening',
  thinking: 'thinking',
  speaking: 'speaking',
};

// How often to check whether the avatar has finished speaking, and how long
// to wait before the first check (speech is queued, then synthesized, before
// the avatar reports itself as speaking).
const SPEAKING_POLL_MS = 250;
const SPEAKING_GRACE_MS = 1500;

const MIC_MUTED_STORAGE_KEY = 'micMuted';

const AvatarContext = createContext({
  avatarRef: { current: null },
  avatarReady: false,
  status: AVATAR_STATUS.idle,
  setStatus: noop,
  // Mute switches off hands-free listening; the caregiver toggles it in the
  // app shell. Persisted so it survives navigation and reloads.
  muted: false,
  setMuted: noop,
  // Returns true when something will be spoken (avatar or browser TTS).
  speakText: () => false,
  stopSpeaking: noop,
  handleAvatarReady: noop,
  handleAvatarLoading: noop,
  // The avatar the caregiver selected (stored URL) and the name it goes by.
  avatarSelection: '',
  assistantName: DEFAULT_ASSISTANT_NAME,
});

export function AvatarProvider({ children }) {
  const avatarRef = useRef(null);
  const [avatarReady, setAvatarReady] = useState(false);
  const [status, setStatus] = useState(AVATAR_STATUS.idle);
  const [muted, setMutedState] = useState(() => {
    try {
      return window.localStorage.getItem(MIC_MUTED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [avatarSelection, setAvatarSelection] = useState(readStoredAvatar);
  useEffect(() => {
    const syncSelection = () => setAvatarSelection(readStoredAvatar());
    window.addEventListener(AVATAR_CHANGED_EVENT, syncSelection);
    window.addEventListener('storage', syncSelection);
    return () => {
      window.removeEventListener(AVATAR_CHANGED_EVENT, syncSelection);
      window.removeEventListener('storage', syncSelection);
    };
  }, []);
  const assistantName = getAssistantNameForId(avatarSelection);
  // Set while browser TTS (the no-avatar fallback) is talking.
  const browserSpeakingRef = useRef(false);

  const setMuted = useCallback((value) => {
    const next = Boolean(value);
    setMutedState(next);
    try {
      window.localStorage.setItem(MIC_MUTED_STORAGE_KEY, String(next));
    } catch {
      // storage unavailable; the in-memory value still applies
    }
  }, []);

  // Speaking ends when the avatar (or browser TTS) reports it is done.
  useEffect(() => {
    if (status !== AVATAR_STATUS.speaking) {
      return undefined;
    }
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt < SPEAKING_GRACE_MS) {
        return;
      }
      const head = avatarRef.current;
      const avatarBusy = !!head && head.isSpeaking === true;
      if (!avatarBusy && !browserSpeakingRef.current) {
        setStatus(AVATAR_STATUS.idle);
      }
    }, SPEAKING_POLL_MS);
    return () => clearInterval(timer);
  }, [status]);

  // 'loading' until the avatar reports in; then 'avatar' (3D avatar speaks)
  // or 'fallback' (it failed to load; browser TTS speaks instead).
  const voiceModeRef = useRef('loading');
  // Speech requested while the avatar was still loading; spoken once ready.
  const pendingSpeechRef = useRef(null);
  // Every speak request gets a token; stopSpeaking invalidates the current
  // one so a late AudioContext.resume() callback cannot speak stale text.
  const speakTokenRef = useRef(0);

  const speakWithBrowser = useCallback((text) => {
    if (!hasBrowserSpeechSynthesisSupport()) {
      return false;
    }
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = getTalkingHeadVoiceConfig().speechLang;
    utterance.onend = () => {
      browserSpeakingRef.current = false;
    };
    utterance.onerror = utterance.onend;
    window.speechSynthesis.cancel();
    browserSpeakingRef.current = true;
    setStatus(AVATAR_STATUS.speaking);
    window.speechSynthesis.speak(utterance);
    return true;
  }, []);

  const speakNow = useCallback((text, token) => {
    const head = avatarRef.current;
    if (voiceModeRef.current !== 'avatar' || !head || typeof head.speakText !== 'function') {
      return speakWithBrowser(text);
    }

    const say = () => {
      if (token !== speakTokenRef.current) {
        return; // cancelled while waiting for the audio context
      }
      setStatus(AVATAR_STATUS.speaking);
      head.speakText(text);
    };

    if (head.audioCtx && head.audioCtx.state === 'suspended') {
      head.audioCtx
        .resume()
        .then(say)
        .catch((err) => console.error('Failed to resume AudioContext:', err));
    } else {
      say();
    }
    return true;
  }, [speakWithBrowser]);

  // A different avatar started loading (the caregiver switched avatars):
  // hold speech until it is ready, as on first load.
  const handleAvatarLoading = useCallback(() => {
    voiceModeRef.current = 'loading';
    setAvatarReady(false);
  }, []);

  const handleAvatarReady = useCallback((ok) => {
    voiceModeRef.current = ok === false ? 'fallback' : 'avatar';
    setAvatarReady(true);

    const pending = pendingSpeechRef.current;
    pendingSpeechRef.current = null;
    if (pending && pending.token === speakTokenRef.current) {
      speakNow(pending.text, pending.token);
    }
  }, [speakNow]);

  const speakText = useCallback((text) => {
    if (!text) {
      return false;
    }

    speakTokenRef.current += 1;
    const token = speakTokenRef.current;

    // Still loading: hold the line until the avatar can say it herself,
    // rather than having a different (browser) voice jump in first.
    if (voiceModeRef.current === 'loading') {
      pendingSpeechRef.current = { text, token };
      return true;
    }

    return speakNow(text, token);
  }, [speakNow]);

  const stopSpeaking = useCallback(() => {
    speakTokenRef.current += 1;
    pendingSpeechRef.current = null;

    const head = avatarRef.current;
    if (head && typeof head.stopSpeaking === 'function') {
      head.stopSpeaking();
    }
    if (hasBrowserSpeechSynthesisSupport()) {
      window.speechSynthesis.cancel();
    }
    browserSpeakingRef.current = false;
    setStatus((current) => (current === AVATAR_STATUS.speaking ? AVATAR_STATUS.idle : current));
  }, []);

  const value = useMemo(
    () => ({
      avatarRef,
      avatarReady,
      status,
      setStatus,
      muted,
      setMuted,
      speakText,
      stopSpeaking,
      handleAvatarReady,
      handleAvatarLoading,
      avatarSelection,
      assistantName,
    }),
    [
      avatarReady,
      status,
      muted,
      setMuted,
      speakText,
      stopSpeaking,
      handleAvatarReady,
      handleAvatarLoading,
      avatarSelection,
      assistantName,
    ]
  );

  return <AvatarContext.Provider value={value}>{children}</AvatarContext.Provider>;
}

export function useAvatar() {
  return useContext(AvatarContext);
}
